import { useEffect, useState } from 'react'
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import {
  getAgentCheck,
  getFleetIdentity,
  getQueuedBody,
  type AgentCheckMatch,
  type AgentQueue,
  type FleetIdentityRow,
  type QueuedMessage,
} from '../transport/api'
import { onEdgeEvent, onStreamOpen } from '../servers/commandStream'
import { onRelayTargetChange, relayTarget } from '../transport/relay'
import { Icon } from '../ui/Icon'
import { parseInbound } from './inbound'

/**
 * Re-read the queues the moment the edge says they changed — owner, 2026-08-11:
 * queue 按鈕浮現的 latency 太高, 這是不是又是啥 heart beat 的爛算法?
 *
 * The opposite, and worse: NOTHING asked. The bell obeys the no-heartbeat rule
 * (no interval anywhere), and the `held` frame commandStream still parses is a
 * v1 vestige the portless edge never sends — an event lane with a listener and
 * no sender. So `['agent-queues']` only refreshed on mount and focus, and a
 * message held while you watched appeared whenever you next happened to touch
 * the window. The edge now emits `queue_changed` from its save() choke point
 * (every enqueue/flush/discard/reap passes it); this is the subscribing half.
 *
 * `refetchQueries`, not `invalidateQueries` — same reason as useIdleStatus:
 * invalidation leaves the fetch to the focus manager, which a WebView does not
 * reliably satisfy. The event means "it changed"; the honest response is "get
 * it now". Callers: QueueBell (every composer) and the sidebar badge — the
 * shared key dedupes their concurrent refetches into one request.
 *
 * THE EVENT CANNOT BE THE ONLY WAY — owner, 2026-09-28: message queue 是空的, 卻有
 * 提示按鈕跑出來? The edge had emptied its queue at 00:14:03 and said so; the bell
 * still showed 1 minutes later. This lane is latency, not delivery-of-record: the BFF
 * swaps an event for a bare re-read when a socket's queue is full, an edge whose link
 * is down drops it, and a parked socket never sees it. With focus refetch measured
 * dead in a WebView, a lost `queue_changed` left the count standing until a remount.
 * So the store is re-read at the moments a loss becomes knowable — the account
 * stream reopening (anything pushed while it was down is gone) and the app coming
 * back into view — plus, in QueueBell, the moment someone opens the sheet to look.
 * Events, all of them; no clock.
 */
/**
 * ONE RE-READ AT A TIME, FOR ALL WHO ASK — aowl, 2026-10-09: every agent on the machine
 * read "couldn't reach aowl" for eleven minutes.
 *
 * An edge said `queue_changed` twice a second for as long as one message was held (its
 * release pump looking at a busy pane; fixed in the edge, and edges that predate the fix
 * are still out there). Each caller of the hook below — the rail, and a bell under every
 * composer — answered each event with `refetchQueries`, which by default CANCELS a read
 * that is out and starts another; the relayed request it cancelled had already left. So
 * a screen sent one read per reader per event, and a machine that answered slowly had
 * more of them outstanding, not fewer. Two Mac screens sent 9,100 queue reads in eleven
 * minutes; the machine's request lane fell a hundred seconds behind.
 *
 * The rule, kept here rather than in each caller: per query client, at most ONE re-read
 * is out. Every reader that hears the same event shares it (they are told in the same
 * tick), and anything that arrives while it is out costs exactly one more, started after
 * the last of them — so the picture it brings is never older than the news. A slow
 * machine now gets asked less often, which is the direction it should go.
 */
const KEY = ['agent-queues'] as const
/** What is shown for a machine that has not answered yet: nothing — never another machine's answer. */
const NOTHING_YET = { ok: true, total: 0, queues: [] as AgentQueue[] }
type Reread = {
  due: boolean
  out: boolean
  again: boolean
  /** The count the machine last announced, for judging the read that announcement earns. */
  said: number | null
  /** A count whose announcement brought nothing new, and the picture it was judged against — see `heard`. */
  repeat: { total: number; picture: string } | null
  /** Open sheets on this screen that say WHY a message waits — see useQueueReasonsLive. */
  watching: number
}
const rereads = new WeakMap<QueryClient, Reread>()

function stateOf(qc: QueryClient): Reread {
  let st = rereads.get(qc)
  if (!st) {
    st = { due: false, out: false, again: false, said: null, repeat: null, watching: 0 }
    rereads.set(qc, st)
  }
  return st
}

/** Who holds what, in order — the same picture the edge ties its announcement to. */
function pictureOf(d: unknown): string | null {
  const queues = (d as { queues?: AgentQueue[] } | undefined)?.queues
  if (!Array.isArray(queues)) return null
  return queues
    .map((q) => `${q.to_host ?? ''}\u0000${q.to_session}\u0001${(q.queued ?? []).map((m) => m.id).join(',')}`)
    .join('\u0002')
}

function rereadQueues(qc: QueryClient): void {
  const state = stateOf(qc)
  if (state.out) {
    state.again = true
    return
  }
  if (state.due) return
  state.due = true
  queueMicrotask(() => {
    state.due = false
    state.out = true
    // A read someone else started (a mount) is joined rather than raced — and then
    // followed by one of our own, because it left before this news did.
    const joined = qc.isFetching({ queryKey: KEY }) > 0
    const before = pictureOf(qc.getQueryData(KEY))
    const said = state.said
    void qc
      .refetchQueries({ queryKey: KEY }, { cancelRefetch: false })
      .catch(() => undefined)
      .finally(() => {
        state.out = false
        /*
         * A READ THAT FAILED IS OWED. The edge now says each change once, so the read
         * that one announcement earns is the only one there will be — and a machine
         * that is behind (today's condition) does not answer it. refetchQueries
         * swallows the failure; counting it as "read" left the bell dark for a message
         * that was held, for the rest of the hold. Paid by `heard`, below.
         */
        const q = qc.getQueryState(KEY)
        const failed = q?.status === 'error'
        /*
         * What that announcement brought. Nothing new — the same picture, and the count
         * it named — makes the next announcement of that count a repeat (see `heard`).
         * ONLY IF NOTHING ELSE WAS SAID WHILE THIS READ WAS OUT: the read answers the
         * announcement that started it, and a later one is not judged by it.
         */
        const after = pictureOf(q?.data)
        const total = (q?.data as { total?: number } | undefined)?.total
        state.repeat =
          !failed && !joined && said !== null && state.said === said && after !== null && after === before && total === said
            ? { total: said, picture: after }
            : null
        if (state.again || joined) {
          state.again = false
          rereadQueues(qc)
        }
      })
  })
}

/** A re-read nothing may talk us out of: the stream came back, the app came back into
 *  view, the screen now points at another machine. */
function mustReread(qc: QueryClient): void {
  const state = stateOf(qc)
  state.said = null
  state.repeat = null
  rereadQueues(qc)
}

/**
 * What to do about one event from a machine.
 *
 * ITS OWN MACHINE ONLY. The read goes to the machine this screen points at
 * (getAgentQueues names none), so an announcement from any OTHER machine of the account
 * used to buy a read of a queue that had not changed — and during the incident a screen
 * pointed at the MacBook re-read the MacBook 2,491 times for aowl's noise.
 *
 * A REPEAT IS BELIEVED TWICE. Edges from before 2026-10-09 (prod's, and any machine that
 * has not updated) say the same count twice a second for as long as mail is held. The
 * announcement carries its count; when the read it earned came back with the same
 * picture as before and that same count, the next announcement of that count is a
 * repeat and is not read — FOR AS LONG AS THAT PICTURE IS THE ONE ON SCREEN. The belief
 * is about a picture, not about a number: the moment any reader changes what is shown
 * (someone presses the bell, a composer mounts) the same count is news again. The first
 * form of this rule kept the number alone, and a later held message of the same count
 * got no bell (second review, reproduced). It is also dropped the moment an
 * announcement is accepted, so one that arrives while that read is out is not judged
 * against what was believed before it.
 */
function heard(qc: QueryClient, event: string, data: Record<string, unknown>): void {
  const from = data['device_id']
  const named = typeof from === 'string' && !!from
  const here = relayTarget()
  if (named && here && from !== here) return
  const state = stateOf(qc)
  // Asked of the cache, not kept in a counter: whoever read last — this lane, the person
  // pressing the bell, a composer mounting — a picture in error is a read that is owed.
  const failing = qc.getQueryState(KEY)?.status === 'error'
  if (event !== 'queue_changed') {
    // An event that names no machine is nobody in particular speaking.
    if (!named) return
    if (failing) {
      /*
       * Any word from this machine is proof it speaks: pay the read that failed — AND
       * KEEP PAYING, one at a time. The form before this paid once and stopped after a
       * second failure in a row; a machine that then recovered was never asked again,
       * and a held message had no bell for the rest of its hold (third review). What
       * bounds it instead is the lane: nothing is started while a read is out, and a
       * read that fails takes its full timeouts (two tries of four seconds, twice) — so
       * a machine that is behind is asked at most about four times in seventeen seconds
       * per screen, however much it says, and not at all once it has answered.
       */
      if (!state.out && !state.due) rereadQueues(qc)
      return
    }
    // An open sheet says WHY each message waits, and that changes with the recipient's
    // busy state, not with the queue (see useQueueReasonsLive).
    if (event === 'idle_status' && state.watching > 0) rereadQueues(qc)
    return
  }
  const total = typeof data['total'] === 'number' ? (data['total'] as number) : null
  const why = data['why']
  if (why === 'standing') {
    // Said once per link by an edge whose link just came up: where the queue stands, after
    // a gap in which anything it said was lost. Never a repeat, whatever count it names.
    mustReread(qc)
    return
  }
  // An edge that says WHY it speaks (ab9ebfc and after) speaks only on a change. The repeat
  // rule is for the edges before it, which name a count and nothing else.
  const onlyOnChange = typeof why === 'string'
  const shown = qc.getQueryData(KEY) as { total?: number } | undefined
  if (
    !onlyOnChange &&
    total !== null &&
    state.repeat !== null &&
    !failing &&
    total === state.repeat.total &&
    shown?.total === total &&
    pictureOf(shown) === state.repeat.picture
  ) {
    return
  }
  state.repeat = null
  state.said = total
  rereadQueues(qc)
}

/**
 * Keep the queue fresh while something on screen says WHY a message waits.
 *
 * The reason ("agent working" / "at a prompt") is computed by the machine on each read,
 * and it changes when the RECIPIENT'S state changes — a turn that ends at a permission
 * prompt writes nothing to the queue, so no queue_changed is said. Until 2026-10-09 the
 * sheet always said the vague "busy" (it read a field the edge never sent), and an edge's
 * noise re-read it twice a second anyway. Now that it names the reason and nothing
 * re-reads by accident, an open sheet would go on saying "agent working" — wait for
 * nothing — for the one wait that needs a person. The machine does say when its agents'
 * busy or asking sets change (idle_status); while a sheet is open that is a reason to
 * read. Closed, it is not: one read per change of state, and only when someone is looking.
 */
export function useQueueReasonsLive(open: boolean): void {
  const qc = useQueryClient()
  useEffect(() => {
    if (!open) return
    const state = stateOf(qc)
    state.watching += 1
    return () => {
      state.watching -= 1
    }
  }, [qc, open])
}

export function useQueueSignal() {
  const qc = useQueryClient()
  useEffect(() => {
    const reread = () => mustReread(qc)
    const offEvent = onEdgeEvent((event, data) => heard(qc, event, data))
    const offOpen = onStreamOpen(reread)
    /*
     * THE QUEUE ON SCREEN IS ONE MACHINE'S, and which machine changes when the person
     * opens an agent somewhere else. Nothing re-read then: the bell kept the previous
     * machine's count until its next announcement — under a second while edges made
     * noise, the whole hold now that they do not.
     *
     * AND ONE MACHINE'S ANSWER IS NEVER DRAWN AS ANOTHER'S. A read to the old machine
     * may still be out (it was slow — that is often why the person left), and the new
     * machine may be away. So the switch cancels what is out and shows nothing until the
     * new machine answers: an unknown is drawn as nothing waiting, not as the last
     * machine's count under this machine's agent.
     */
    const offTarget = onRelayTargetChange(() => {
      void qc.cancelQueries({ queryKey: KEY })
      qc.setQueriesData({ queryKey: KEY }, NOTHING_YET)
      reread()
    })
    const onVisible = () => {
      if (document.visibilityState === 'visible') reread()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      offEvent()
      offOpen()
      offTarget()
      document.removeEventListener('visibilitychange', onVisible)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

/**
 * The hands a caller can put on individual messages. All optional: Activity's queue
 * tab draws the same rows with none of them and stays a picture; the composer's
 * sheet passes all four and becomes a control.
 */
export type QueueActions = {
  /** Which ids are ticked. */
  selected?: ReadonlySet<string>
  onSelect?: (id: string, on: boolean) => void
  /** Deliver / discard these ids — one row's, one sender's, or the selection's. */
  onDeliver?: (ids: string[]) => void
  onDiscard?: (ids: string[]) => void
  /** An action is in flight: every control in the rows waits for it. */
  busy?: boolean
}

/**
 * Who is holding whose mail — one recipient's queue, drawn as rows.
 *
 * A message is INJECTED into the recipient's pane, so one delivered while that agent
 * sits at a prompt used to be pasted into the menu and submitted with Enter — the
 * message vanished AND answered the question, while the sender was told it arrived.
 * The server now holds them and flushes when the prompt clears.
 *
 * Which makes a waiting message invisible to everyone who cares: the recipient does
 * not know it exists, and the person at the menu does not know anyone tried to reach
 * them. This is that state made visible, and the owner asked for it as a picture of
 * WHO SENT TO WHOM rather than an unread count — so every row names both ends.
 *
 * GROUPED BY SENDER INSIDE THE RECIPIENT (2026-09-05). The owner's ask is to act on
 * "each field": per recipient (the group), per sender→recipient pair (the sub-group,
 * with its own pair of buttons), and per message (the row's own pair). The
 * whole-recipient buttons stay on the sheet's row; nothing that existed was taken
 * away, and "deliver everything" is now the choice you make when you have looked.
 *
 * THIS FILE USED TO OWN A SECOND DOOR ONTO ITSELF. `QueuePanel` was an orange
 * "N queued" chip pinned to the top-right of Clean View, expanding to exactly these
 * rows. Removed 2026-08-22 — owner: 右上方這個 queued 標籤可以移除了。它的功能跟
 * Text edit 這裡的按鈕重複. It was: the composer's QueueBell reads the same
 * `['agent-queues']` key, shows the same count, renders these same groups, and adds
 * the Deliver / Discard the chip never had. Two controls for one fact, one of them a
 * strict subset, on the corner of the view you read.
 *
 * What survives here is what more than one door needs: these rows (QueueBell's sheet,
 * Activity's queue tab), the refetch signal, and the wait formatter.
 */
export function QueueGroup({ qu, actions }: { qu: AgentQueue; actions?: QueueActions }) {
  // One open at a time: the panel is narrow, and two long bodies at once turn it
  // into a wall that hides the queue it exists to show.
  const [expanded, setExpanded] = useState<string | null>(null)
  /*
   * Fetched bodies, kept by id. A message that flushes while open keeps the text on
   * screen — the reader was mid-sentence, and yanking it to show "delivered" would
   * lose what they were reading to tell them something the row already implies.
   */
  const [bodies, setBodies] = useState<Record<string, string>>({})

  const toggle = async (id: string) => {
    if (expanded === id) {
      setExpanded(null)
      return
    }
    setExpanded(id)
    if (bodies[id] !== undefined) return
    try {
      const r = await getQueuedBody(id)
      const parsed = parseInbound(r.body)
      setBodies((b) => ({ ...b, [id]: (parsed?.body ?? r.body).trim() }))
    } catch {
      /*
       * A 404 here is a RACE, not a fault: the message flushed between the list
       * arriving and this click. Saying so is the whole point — an empty body would
       * read as "this message had no content", which is a different claim and a
       * confidently wrong one. Same shape as everything else this week: an empty
       * result cannot tell "nothing" from "not here any more".
       */
      setBodies((b) => ({ ...b, [id]: 'Delivered while you were reading it.' }))
    }
  }

  const acts = actions ?? {}
  const canAct = !!(acts.onDeliver || acts.onDiscard)
  const bySender = groupBySender(qu.queued)

  return (
    <div className="qp-group">
      <div className="qp-to">
        {/* The recipient, and WHY it is still waiting. Three states, because the
            server reports three: waiting on a prompt, about to flush, or a remote
            edge whose state this one cannot see. Collapsing "unknown" into "not
            blocked" would state something nobody checked. */}
        <span className="qp-arrow" aria-hidden="true">
          →
        </span>
        <span className="qp-name">{qu.to_label ?? qu.to_session}</span>
        <span className={`qp-state${qu.blocked === true ? ' blocked' : ''}`}>
          {whyWaiting(qu)}
        </span>
      </div>
      {bySender.map((grp) => {
        const ids = grp.messages.map((m) => m.id)
        const sample = grp.messages[0]!
        return (
          <div className="qp-sender" key={grp.from_hash}>
            <div className="qp-senderhd">
              <SenderTag m={sample} />
              <span className="qp-sendern">
                {grp.messages.length} message{grp.messages.length === 1 ? '' : 's'}
              </span>
              {canAct && (
                <span className="qp-msgacts">
                  {acts.onDeliver && (
                    <button
                      type="button"
                      className="qp-act"
                      disabled={acts.busy}
                      title="Deliver every message from this sender"
                      aria-label={`Deliver every message from ${senderName(sample)}`}
                      onClick={() => acts.onDeliver?.(ids)}
                    >
                      <Icon name="send" size={13} label="Deliver" />
                    </button>
                  )}
                  {acts.onDiscard && (
                    <button
                      type="button"
                      className="qp-act danger"
                      disabled={acts.busy}
                      title="Discard every message from this sender"
                      aria-label={`Discard every message from ${senderName(sample)}`}
                      onClick={() => acts.onDiscard?.(ids)}
                    >
                      <Icon name="delete" size={13} label="Discard" />
                    </button>
                  )}
                </span>
              )}
            </div>
            {grp.messages.map((m) => {
              /*
               * The preview arrives FRAMED — `<agent-msg:HASH> … </agent-msg:HASH>` —
               * and may be cut mid-tag at 80 characters. `parseInbound` strips it and
               * returns null when there is nothing to strip, so a plain message shows
               * as itself. Left framed, every row would open with a hash the row
               * already names.
               */
              const parsed = parseInbound(m.preview)
              const preview = (parsed?.body ?? m.preview ?? '').trim()
              const open = expanded === m.id
              const full = open ? bodies[m.id] : undefined
              const ticked = acts.selected?.has(m.id) ?? false
              return (
                <div className={`qp-msg${ticked ? ' ticked' : ''}`} key={m.id}>
                  {acts.onSelect && (
                    <input
                      type="checkbox"
                      className="qp-check"
                      checked={ticked}
                      disabled={acts.busy}
                      aria-label={`Select message from ${senderName(m)}`}
                      onChange={(e) => acts.onSelect?.(m.id, e.target.checked)}
                    />
                  )}
                  <div className="qp-msgbody">
                    <div className="qp-from">
                      <span className="qp-wait">{waited(m.waited_s)}</span>
                    </div>
                    {preview && (
                      /*
                       * EVERY ROW OPENS — owner, 2026-09-05: 每一個 Agent 所傳出來的單一條
                       * 訊息,其實都應該可以被點擊展開來看內容,這樣子使用者才有更多的選擇,到
                       * 底該不該把它傳送出去. The text itself is the control: click (or
                       * Enter/Space) and the whole message unfolds under it, click again
                       * and it folds. A person deciding deliver-or-discard needs the
                       * words, not the first eighty of them.
                       *
                       * `has_more` still means what it meant: the server saying it cut
                       * the preview. It now only chooses the hint word ("more") on rows
                       * where the preview is known to be short of the message — a
                       * preview that ends cleanly used to read like a whole message,
                       * which is how the owner took one for a placeholder.
                       *
                       * The body is fetched on open rather than shipped in every list
                       * row: it is uncapped, up to 50 are held per recipient, and the
                       * list is re-read on every change — the cost peaked exactly when
                       * the queue was worth looking at.
                       */
                      <div
                        className={`qp-preview${open ? ' open' : ''}`}
                        role="button"
                        tabIndex={0}
                        aria-expanded={open}
                        title={open ? 'Click to fold the message' : 'Click to read the whole message'}
                        onClick={() => void toggle(m.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            void toggle(m.id)
                          }
                        }}
                      >
                        {open ? (full ?? (m.has_more ? `${preview}…` : preview)) : preview}
                        <span className="qp-more" aria-hidden={!m.has_more && !open}>
                          {open ? (full === undefined ? 'loading…' : 'less') : m.has_more ? 'more' : ''}
                        </span>
                      </div>
                    )}
                  </div>
                  {canAct && (
                    <span className="qp-msgacts">
                      {acts.onDeliver && (
                        <button
                          type="button"
                          className="qp-act"
                          disabled={acts.busy}
                          title="Deliver this message"
                          aria-label="Deliver this message"
                          onClick={() => acts.onDeliver?.([m.id])}
                        >
                          <Icon name="send" size={13} label="Deliver" />
                        </button>
                      )}
                      {acts.onDiscard && (
                        <button
                          type="button"
                          className="qp-act danger"
                          disabled={acts.busy}
                          title="Discard this message"
                          aria-label="Discard this message"
                          onClick={() => acts.onDiscard?.([m.id])}
                        >
                          <Icon name="delete" size={13} label="Discard" />
                        </button>
                      )}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

/**
 * Messages by sender, senders in order of their first message. A stable order so a
 * row does not jump to another sub-group when a sibling is delivered.
 */
function groupBySender(
  queued: QueuedMessage[],
): Array<{ from_hash: string; messages: QueuedMessage[] }> {
  const out: Array<{ from_hash: string; messages: QueuedMessage[] }> = []
  for (const m of queued) {
    const key = (m.from_hash ?? '').toLowerCase()
    const g = out.find((x) => x.from_hash === key)
    if (g) g.messages.push(m)
    else out.push({ from_hash: key, messages: [m] })
  }
  return out
}

/**
 * Why this queue is still waiting, in the words a reader can act on.
 *
 * `blocked: true` is one spinner for two situations that need opposite reactions:
 * at a prompt means another AGENT has to answer, while typing/speaking means a
 * PERSON has to stop. The server separates them, so this must not fold them back
 * together — and it falls back to the plain boolean when the reason is absent rather
 * than inventing one.
 */
function whyWaiting(qu: AgentQueue): string {
  // The edge has always sent this as `blocked_why`; the name read here was never sent, so
  // every wait read as "busy" — including the one that needs a person (a prompt).
  switch (qu.blocked_reason ?? qu.blocked_why) {
    case 'at_prompt':
      return 'at a prompt'
    case 'typing':
      return 'user typing'
    case 'speaking':
      return 'user speaking'
    case 'working':
      /*
       * The AGENT is mid-turn, and this is the only one of the four that clears
       * itself — the server releases a `working` hold after 120s even if the turn
       * never visibly ends, precisely so a latched signal cannot strand a message
       * with nobody coming to free it.
       *
       * Worth its own word rather than the generic "busy": `at a prompt` means go
       * and answer something, `user typing` means wait for a person, and this one
       * means wait for nothing at all.
       */
      return 'agent working'
    default:
      return qu.blocked === true ? 'busy' : qu.blocked === false ? 'delivering' : 'unknown'
  }
}

/** The sender's own label or hash — for aria text, where the resolved name may not
 *  have arrived yet and a bare hash is still a name. */
function senderName(m: { from_hash: string; from_label?: string | null }): string {
  return m.from_label ?? m.from_hash
}

/**
 * The sender, as concisely as this client can NAME it — owner, 2026-08-11:
 * 解析傳訊者的信息, 讓 agent name @ server / owner 可以精簡的顯示.
 *
 * Resolution order is a trust order, richest first:
 *  1. the account fleet (same-account agents; carries the machine) → `name @ machine`
 *  2. the cloud's `/agent/check` (foreign-account senders — the case the
 *     blocking UI exists for) → `name @ machine / owner`, owner shown because
 *     for a foreign sender WHOSE it is IS the information
 *  3. `from_label` — the queue-holding edge's own resolution (a name, no machine)
 *  4. the bare hash, which is the honest floor, never an error
 * Same-account rows do NOT print the owner: it would be this account's own name
 * on every row, which is noise against the one row where owner matters.
 */
export function senderLabel(
  m: { from_hash: string; from_label?: string | null },
  fleet: FleetIdentityRow[] | undefined,
  checked: AgentCheckMatch[] | undefined,
): string {
  const want = m.from_hash.toLowerCase()
  const f = fleet?.find((r) => r.hash.toLowerCase() === want)
  if (f) return `${f.session} @ ${f.machine}`
  const c = checked?.[0]
  if (c) {
    const owner = c.owner_name || c.owner_email
    return `${c.session} @ ${c.machine}${owner ? ` / ${owner}` : ''}`
  }
  return m.from_label ?? m.from_hash
}

/**
 * `qp-who` with the hash resolved. The check query fires only after the fleet
 * has answered AND missed — most rows are same-account and cost nothing extra —
 * and its answer is cached per hash for the session (identities change on
 * attach/rename, not per message). Every failure path lands on the fallback
 * chain in senderLabel; the raw hash stays reachable in the tooltip.
 */
function SenderTag({ m }: { m: { from_hash: string; from_label?: string | null } }) {
  const fleetQ = useQuery({
    queryKey: ['fleet-identity'],
    queryFn: getFleetIdentity,
    staleTime: 60_000,
  })
  const fleet = fleetQ.data?.agents
  const inFleet = !!fleet?.some((r) => r.hash.toLowerCase() === m.from_hash.toLowerCase())
  const checkQ = useQuery({
    queryKey: ['agent-check', m.from_hash.toLowerCase()],
    queryFn: () => getAgentCheck(m.from_hash),
    enabled: !!fleet && !inFleet,
    staleTime: 10 * 60_000,
    retry: false, // the route degrades to ok:false itself; retrying re-asks a cloud that already answered
  })
  return (
    <span className="qp-who" title={m.from_hash}>
      {senderLabel(m, fleet, checkQ.data?.matches)}
    </span>
  )
}

/** How long it has waited, in the coarsest unit that still distinguishes cases. */
export function waited(s: number): string {
  // A remote edge's queue row can arrive without the field (measured: a
  // cross-machine hold rendered "NaNh" — owner screenshot, 2026-08-11). An
  // absent duration is absent, not a number to format.
  if (!Number.isFinite(s)) return ''
  // Seconds up to a minute, then minutes. "just now" versus "9 minutes" is the
  // difference that changes what the reader does; 63s versus 64s is not.
  if (s < 60) return `${Math.round(s)}s`
  if (s < 3600) return `${Math.round(s / 60)}m`
  return `${Math.round(s / 3600)}h`
}
